import { useEffect, useState } from 'react'
import { View, Text, Pressable, StyleSheet } from 'react-native'
import ReanimatedAnimated, { useSharedValue, useAnimatedStyle, withSpring, interpolate } from 'react-native-reanimated'
import { useThemeColors } from '../../lib/useThemeColors'
import { fonts } from '../../lib/theme'
import { hapticLight } from '../../lib/haptics'
import { useCountdown } from './ui'

// ─── WalletStack ─────────────────────────────────────────────────────────
// Card impilate in verticale, stile Wallet: di ogni card dietro si vede solo la striscia in alto
// (nome gara + conto alla rovescia), quella selezionata sta davanti, intera, in fondo al mazzo.
// Si tocca una striscia per portarla davanti: le card si riposizionano con una molla (solo
// movimento verticale, niente rotazione/scala/dissolvenza) e la pagina resta scrollabile
// perche' non c'e' nessun gesto di trascinamento.
// Le strisce fanno da indicatore (come i pallini nei carousel): niente pallini.
//
// items: [{ key, label, date, time, card }] — in ordine di scadenza (la prima e' la piu' vicina).
const STRIP = 46          // altezza visibile di una card dietro
const SPRING = { damping: 20, stiffness: 220, mass: 0.9 }

export function MiniCountdown({ date, time }) {
  const tc = useThemeColors()
  const diff = useCountdown(date, time)
  if (!diff) return null
  const pad = (v) => String(v).padStart(2, '0')
  const txt = `${diff.days > 0 ? pad(diff.days) + ':' : ''}${pad(diff.hours)}:${pad(diff.minutes)}:${pad(diff.seconds)}`
  return <Text style={[s.mini, { color: diff.urgent ? tc.rosaText : tc.textMuted }]}>{txt}</Text>
}

function WalletCard({ y, z, front, onPress, label, strip, onHeight, children }) {
  const tc = useThemeColors()
  const ty = useSharedValue(y)
  const dim = useSharedValue(front ? 0 : 1)
  // ty/dim seguono il bersaglio con una molla; z cambia subito (la card che sale deve stare sopra)
  useEffect(() => {
    ty.value = withSpring(y, SPRING)
    dim.value = withSpring(front ? 0 : 1, SPRING)
  }, [y, front])

  const style = useAnimatedStyle(() => ({ transform: [{ translateY: ty.value }] }))
  const dimStyle = useAnimatedStyle(() => ({ opacity: interpolate(dim.value, [0, 1], [0, 0.35]) }))

  return (
    <ReanimatedAnimated.View
      style={[s.card, { zIndex: z, backgroundColor: tc.bg }, style]}
      onLayout={(e) => onHeight(e.nativeEvent.layout.height)}
    >
      <View pointerEvents={front ? 'auto' : 'none'}>{children}</View>
      {!front && (
        <>
          <ReanimatedAnimated.View pointerEvents="none" style={[s.dim, { backgroundColor: tc.bg }, dimStyle]} />
          <View pointerEvents="none" style={s.stripRight}>{strip}</View>
          <Pressable
            style={s.stripHit}
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={label}
          />
        </>
      )}
    </ReanimatedAnimated.View>
  )
}

export default function WalletStack({ items }) {
  const [selected, setSelected] = useState(0)
  const [heights, setHeights] = useState({})
  const n = items.length
  const sel = Math.min(selected, n - 1)
  const H = Math.max(160, ...Object.values(heights))

  // posizione verticale: le card dietro in ordine di scadenza, la selezionata ultima (davanti)
  const others = items.map((_, i) => i).filter(i => i !== sel)
  const slot = (i) => (i === sel ? n - 1 : others.indexOf(i))

  return (
    <View style={{ height: (n - 1) * STRIP + H }}>
      {items.map((it, i) => {
        const k = slot(i)
        return (
          <WalletCard
            key={it.key}
            y={k * STRIP}
            z={k + 1}
            front={i === sel}
            label={it.label}
            strip={it.strip}
            onPress={() => { hapticLight(); setSelected(i) }}
            onHeight={(h) => setHeights(prev => (prev[i] === h ? prev : { ...prev, [i]: h }))}
          >
            {it.card}
          </WalletCard>
        )
      })}
    </View>
  )
}

const s = StyleSheet.create({
  card: { position: 'absolute', left: 0, right: 0, top: 0, borderRadius: 14 },
  dim: { ...StyleSheet.absoluteFillObject, borderRadius: 14 },
  stripHit: { position: 'absolute', top: 0, left: 0, right: 0, height: STRIP },
  stripRight: { position: 'absolute', top: 0, right: 14, height: STRIP, justifyContent: 'center' },
  mini: { fontFamily: fonts.condensedBold, fontSize: 15, letterSpacing: 0.5 },
})
